<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    :subtitle="plugin.name"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="60%"
  >
    <v-tabs centered v-model="selectedTab">
      <v-tab>{{ $t("General") }}</v-tab>
      <v-tab>{{ $t("Settings") }}</v-tab>
      <v-tab-item>
        <v-card flat class="my-0">
          <v-card-text>
            <v-form class="mt-5">
              <v-text-field
                v-bind:label="$t('Name')"
                v-model="plugin.name"
                :error-messages="nameErrors"
                @input="$v.plugin.name.$touch()"
                @blur="$v.plugin.name.$touch()"
              ></v-text-field>
              <v-select
                multiple
                v-model="plugin.contentTypeIDs"
                :items="contentTypesList"
                item-text="text"
                item-value="id"
                v-bind:label="$t('ContentTypes')"
                :error-messages="contentTypeIDsErrors"
                @change="$v.plugin.contentTypeIDs.$touch()"
                @blur="$v.plugin.contentTypeIDs.$touch()"
              ></v-select>

              <v-select
                v-model="plugin.location"
                :error-messages="locationErrors"
                @change="$v.plugin.location.$touch()"
                @blur="$v.plugin.location.$touch()"
                :items="pluginLocations"
                item-text="text"
                item-value="id"
                v-bind:label="$t('Location')"
              ></v-select>
              <BaseIconPicker
                :label="$t('Icon')"
                v-model="plugin.icon"
                :error-messages="iconErrors"
                @input="$v.plugin.icon.$touch()"
                @blur="$v.plugin.icon.$touch()"
              />
              <v-row dense>
                <v-col>
                  <v-file-input
                    v-if="allowEditPluginContent"
                    v-bind:label="$t('File')"
                    v-model="pluginContent"
                    accept=".umd.min.js"
                  ></v-file-input>
                  <v-text-field
                    readonly
                    v-if="!allowEditPluginContent"
                    v-model="plugin.pluginContentFileName"
                    :label="$t('File')"
                    append-icon="mdi-pencil"
                    @click:append="editPluginContent()"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-switch
                v-if="plugin.id <= 0"
                v-model="plugin.automaticAuthorization"
                :label="$t('AutomaticAuthorization')"
              ></v-switch>
            </v-form>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card class="my-3">
          <v-card-text>
            <v-row align="center">
              <v-col cols="12">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      small
                      class="primary"
                      v-on="on"
                      @click="setJsonEditMode('TreeView')"
                    >
                      <v-icon>mdi-file-tree</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("TreeView") }}</span>
                </v-tooltip>

                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      small
                      class="primary ml-2"
                      v-on="on"
                      @click="setJsonEditMode('Text')"
                    >
                      <v-icon>mdi-code-json</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("TextEditor") }}</span>
                </v-tooltip>
              </v-col>
            </v-row>
            <div v-if="jsonEditMode == 'TreeView'">
              <JsonEditor
                :options="configOptions"
                :objData="plugin.pluginConfig"
                v-model="plugin.pluginConfig"
              >
              </JsonEditor>
            </div>
            <div v-if="jsonEditMode == 'Text'">
              <v-jsoneditor
                v-model="plugin.pluginConfig"
                :options="textEditorConfigOptions"
                height="400px"
                @error="onError"
                @input="changeLog"
              />
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs>
  </base-modal>
</template>
<script>
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

import PluginService from '@/services/PluginService';
import Plugin from '@/ecm/models/plugin/Plugin';
import Utils from '@/ecm/common/Utils';
import PluginLocations from '@/ecm/variables/PluginLocations';

export default {
    name: 'pluginModal',
    created() {
        this.$v.$reset();
    },
    data() {
        return {
            modal: {
                title: this.$t('Plugin'),
                key: 'pluginModal',
            },
            pluginLocations: [
                { id: PluginLocations.ResultViewDocument, text: this.$t('Document') },
                { id: PluginLocations.ResultViewToolbar, text: this.$t('Toolbar') },
            ],
            open: false,
            contentTypesList: [],
            plugin: new Plugin(),
            allowEditPluginContent: true,
            pluginContent: {},
            selectedTab: 0,
            jsonEditMode: 'TreeView',
            configOptions: {
                confirmText: this.$t('Save'),
                cancelText: this.$t('Cancel'),
            },
            textEditorConfigOptions: {
                mode: 'code',
            },
            isNew: true,
            hasError: false,
        };
    },
    mixins: [validationMixin],
    validations: {
        plugin: {
            name: { required },
            location: { required },
            icon: { required },
            contentTypeIDs: { required },
        },
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        modalTitle() {
            return (
                this.modal.title + (this.plugin.name ? ' - ' + this.plugin.name : '')
            );
        },
        nameErrors() {
            const errors = [];
            if (!this.$v.plugin.name.$dirty) return errors;
            !this.$v.plugin.name.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        locationErrors() {
            const errors = [];
            if (!this.$v.plugin.location.$dirty) return errors;
            !this.$v.plugin.location.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        iconErrors() {
            const errors = [];
            if (!this.$v.plugin.icon.$dirty) return errors;
            !this.$v.plugin.icon.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        contentTypeIDsErrors() {
            const errors = [];
            if (!this.$v.plugin.contentTypeIDs.$dirty) return errors;
            !this.$v.plugin.contentTypeIDs.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
    },
    watch: {
        pluginContent() {
            if (this.pluginContent) {
                Utils.file.toBase64(this.pluginContent).then((base64) => {
                    this.plugin.pluginContentBase64 = Utils.file.image.getBase64(
                        base64
                    ).data;
                    this.plugin.pluginContentFileName = this.pluginContent.name;
                });
            }
        },
    },
    methods: {
        onError() {
            this.hasError = true;
        },
        changeLog() {
            this.hasError = false;
        },
        setJsonEditMode(editMode) {
            this.jsonEditMode = editMode;
        },
        getContentTypes(id) {
            PluginService.getAvailableContentTypes(id).then((data) => {
                this.contentTypesList = data;
            });
        },
        saveItem() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                this.selectedTab = 0;
                return;
            }
            if (this.hasError) {
                Utils.toast.error(this.$t('Site.Plugins.ErrorConfiguringPlugin'));
                this.selectedTab = 1;
                return;
            }

            this.modalInstance.progress(true);

            PluginService.save(this.plugin.toDto())
                .then((result) => {
                    if (result) {
                        if (this.isNew) {
                            Utils.toast.success(
                                this.$t('Plugin') + ' ' + this.$t('CreatedSuccessfully')
                            );
                        } else {
                            Utils.toast.success(
                                this.$t('Plugin') + ' ' + this.$t('UpdatedSuccessfully')
                            );
                        }
                        this.hide();
                        this.$emit('on-item-saved');
                    }
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.modalInstance.progress(false);
                });
        },
        new() {
            this.plugin = new Plugin();
            this.allowEditPluginContent = true;
            this.getContentTypes();
            this.$v.$reset();
            this.show();
        },
        editPluginContent() {
            this.allowEditPluginContent = true;
            this.plugin.pluginContentFileName = '';
        },
        edit(dto) {
            this.plugin = new Plugin(dto, true);
            this.isNew = false;
            this.getContentTypes(this.plugin.id);
            this.allowEditPluginContent = false;
            this.show();
        },
        show() {
            this.open = true;
            this.selectedTab = 0;
        },
        hide() {
            this.open = false;
            this.selectedTab = 0;
            this.isNew = true;
        },
    },
};
</script>
<style>
.add-form {
  font-size: 1.125rem !important;
}
.pure-button {
  border-radius: 4px;
  background: #999 !important;
  color: white !important;
  box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2),
    0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
}
.pure-form input {
  box-shadow: none !important;
}
.pure-form input:focus {
  border-color: #00cae3 !important;
}
.pure-form select {
  padding-top: 5px !important;
}
.pure-form select:focus {
  border-color: #00cae3 !important;
}
.f-confirm {
  background: #00cae3 !important;
}
.del-btn {
  color: #f44336 !important;
  caret-color: #f44336 !important;
}
.add-key {
  font-size: inherit !important;
  color: #00cae3 !important;
}
.block_content .val-input {
  width: 70% !important;
}
</style>
